import React, { useMemo, useState } from 'react';

type FormState = {
  industry: string;
  teamSize: string;
  techStack: string;
  painPoints: string;
  email: string;
  consent: boolean;
  botField: string;
};

type ApiResponse =
  | { summary: string; readiness: string; nextSteps: string[] }
  | { error: string };

const inputCls = (hasError?: boolean) =>
  `mt-1.5 block w-full rounded-xl bg-slate-50 border ${
    hasError ? 'border-red-400' : 'border-slate-200'
  } text-slate-900 px-4 py-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-em/30 focus:border-em transition-all`;

// Lead-gated AI credibility demo with preview-first flow.
const AIAssessment = () => {
  const [form, setForm] = useState<FormState>({
    industry: '', teamSize: '', techStack: '', painPoints: '',
    email: '', consent: false, botField: '',
  });
  const ASSESSMENT_ENDPOINT = import.meta.env.VITE_ASSESSMENT_ENDPOINT || '/api/assessment';
  const [previewReady, setPreviewReady] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isInitialValid = useMemo(() =>
    form.industry.trim().length > 1 &&
    form.teamSize.trim().length > 0 &&
    form.techStack.trim().length > 3 &&
    form.painPoints.trim().length > 10,
  [form]);

  const emailValid = useMemo(() =>
    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(form.email.trim()),
  [form.email]);

  const buildPreview = () => {
    if (!isInitialValid) { setError('Please complete the required fields to see the preview.'); return; }
    setError(null);
    const shortStack = form.techStack.length > 120 ? `${form.techStack.slice(0, 117)}...` : form.techStack;
    const shortPain  = form.painPoints.length > 200 ? `${form.painPoints.slice(0, 197)}...` : form.painPoints;
    setPreviewText([
      `Industry: ${form.industry}`,
      `Team size: ${form.teamSize || 'Not specified'}`,
      `Stack: ${shortStack || 'Not specified'}`,
      `Primary friction: ${shortPain}`,
      '',
      'What you will receive:',
      '- A concise readiness snapshot tailored to your team size and sector.',
      '- Risks and opportunities for AI adoption based on your stack and pain points.',
      '- 3 targeted next steps to improve credibility and delivery speed.',
    ].join('\n'));
    setPreviewReady(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setResult(null);
    if (!previewReady) { setError('Create a preview first to confirm details.'); return; }
    if (!emailValid || !form.consent) { setError('Add a valid email and confirm consent to proceed.'); return; }
    if (form.botField && form.botField.trim().length > 0) { setError('Submission blocked.'); return; }

    setSubmitting(true);
    try {
      const response = await fetch(ASSESSMENT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          industry: form.industry.trim(), teamSize: form.teamSize.trim(),
          techStack: form.techStack.trim(), painPoints: form.painPoints.trim(),
          email: form.email.trim(), consent: form.consent, botField: form.botField,
        }),
      });
      const data = (await response.json()) as ApiResponse;
      if (!response.ok || 'error' in data) throw new Error('error' in data ? data.error : 'Request failed');
      setResult(data);
    } catch (err: any) {
      setError(err?.message || 'Unable to complete the assessment right now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Page header */}
      <div className="border-b border-slate-100 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-em to-teal flex items-center justify-center text-white text-xs font-bold">
              NT
            </div>
            <span className="font-semibold text-slate-800 hidden sm:block">Nova Terra</span>
          </div>
          <a href="/" className="text-sm text-em-dark hover:text-em-deeper font-medium transition-colors">
            ← Back to site
          </a>
        </div>
        <div className="h-px gradient-em" />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Hero block */}
        <div className="mb-12 space-y-3 max-w-3xl">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-em-light border border-em-border text-xs text-em-deeper font-semibold">
            AI Demo · 90-Day Plan
          </span>
          <h1 className="text-4xl font-bold text-slate-900 leading-tight">90-Day Plan (AI-Developed Demo)</h1>
          <p className="text-slate-500 leading-relaxed">
            Describe your team and friction points. We generate a pragmatic 90-day modernization and operating plan with AI, showing how we blend executive judgment with credible AI outputs. Preview first, then unlock the plan with your email and consent.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} className="lg:col-span-3 relative bg-white rounded-3xl border border-slate-100 shadow-card p-8 space-y-6 overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-0.5 gradient-em" />

            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">Industry *</label>
              <input
                type="text"
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className={inputCls()}
                placeholder="Healthcare, fintech, manufacturing..."
                required
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">Team size *</label>
              <select
                value={form.teamSize}
                onChange={(e) => setForm({ ...form, teamSize: e.target.value })}
                className={inputCls()}
                required
              >
                <option value="">Select a range</option>
                <option value="1-10">1–10</option>
                <option value="11-50">11–50</option>
                <option value="51-200">51–200</option>
                <option value="201-500">201–500</option>
                <option value="500+">500+</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">Core tech stack *</label>
              <textarea
                value={form.techStack}
                onChange={(e) => setForm({ ...form, techStack: e.target.value })}
                className={inputCls()}
                placeholder="Cloud platform, data tools, LLMs, key applications..."
                rows={3}
                required
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">Current pain points *</label>
              <textarea
                value={form.painPoints}
                onChange={(e) => setForm({ ...form, painPoints: e.target.value })}
                className={inputCls()}
                placeholder="Where AI credibility or delivery is breaking down..."
                rows={4}
                required
              />
            </div>

            {/* Preview action */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={buildPreview}
                disabled={!isInitialValid}
                className="inline-flex items-center gap-2 bg-em text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-em-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-glow-em"
              >
                Preview assessment outline
              </button>
              {!previewReady && (
                <p className="text-sm text-slate-400">No AI call yet — see the outline first.</p>
              )}
              {previewReady && (
                <p className="text-sm text-em-deeper font-medium">Preview ready. Add email + consent to continue.</p>
              )}
            </div>

            {/* Preview box */}
            {previewReady && (
              <div className="rounded-2xl border border-em-border bg-em-light p-5">
                <p className="text-[10px] text-em-dark font-bold uppercase tracking-widest mb-3">Preview</p>
                <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans leading-relaxed">{previewText}</pre>
              </div>
            )}

            {/* Email + consent */}
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">Work email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls()}
                  placeholder="you@company.com"
                  required
                />
              </div>

              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-em focus:ring-em/30"
                  required
                />
                <span className="text-sm text-slate-600">I agree to receive the AI assessment summary at this email.</span>
              </label>
            </div>

            {/* Honeypot field to deter bots */}
            <div className="hidden">
              <label htmlFor="botField">Leave this field empty</label>
              <input id="botField" type="text" value={form.botField} onChange={(e) => setForm({ ...form, botField: e.target.value })} tabIndex={-1} autoComplete="off" />
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full btn-shimmer text-white py-3.5 rounded-full text-sm font-bold shadow-glow-em hover:shadow-glow-em-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Generating assessment...' : 'Send me the AI assessment'}
            </button>

            {result && 'summary' in result && (
              <div className="rounded-2xl border border-em-border bg-em-light p-5 space-y-3">
                <p className="text-[10px] text-em-dark font-bold uppercase tracking-widest">Assessment result</p>
                <p className="text-base font-semibold text-slate-900">Assessment summary</p>
                <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{result.summary}</p>
                <p className="text-sm text-slate-500">Readiness: <span className="font-medium text-slate-700">{result.readiness}</span></p>
                <ul className="space-y-1.5">
                  {result.nextSteps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-em flex-shrink-0" />
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </form>

          {/* ── Info sidebar ── */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-3xl border border-slate-100 shadow-card overflow-hidden">
              <div className="h-0.5 gradient-em" />
              <div className="p-6 space-y-4">
                <p className="text-[10px] text-em-dark font-bold uppercase tracking-widest">What this includes</p>
                <ul className="space-y-3">
                  {[
                    'Assessment preview before any AI call.',
                    'AI-developed 90-day plan aligned to your team size and constraints.',
                    'Three actionable next steps with quick wins to build momentum.',
                    'Stored securely for follow-up; emailed to you when enabled.',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-em flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rounded-2xl border border-em-border bg-em-light p-5 space-y-3">
              <p className="text-[10px] text-em-dark font-bold uppercase tracking-widest">How we handle your data</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                We require email and consent before the AI call. Data is used to generate the assessment and stored securely for follow-up. No marketing spam.
              </p>
              <a href="/privacy-policy" className="text-sm text-em-dark hover:text-em-deeper font-medium transition-colors">
                View Privacy Policy →
              </a>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AIAssessment;
